import { Component, EventEmitter, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';

import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { EfileEditSubmissionTableDataService } from './efile-edit-submission-table-data.service';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  sanitizeActionParams,
} from '../../shared/workspace-element-context';

/**
 * Modal form for creating / editing a single `efile_submission` row. The
 * component is hosted inside `CeHostDialogComponent`, which parks the dialog
 * payload on `WorkspaceService.workspaceFactory.activeScreen.data` before the
 * custom element is created (see `CeHostDialogComponent.bridgeDataToActiveScreen`).
 *
 * All runtime context (row to edit, grid column metadata, base URLs,
 * USER_SETTINGS, filterParams) is read from the injected global services —
 * NOT from `@Input` HTML attributes. This matches the rest of the migrated
 * modal components (e.g. `EfileSubmissionManifestTableComponent`) and avoids
 * the JSON-string attribute contract that previously required setters + parse
 * + try/catch for each piece of data.
 */
@Component({
    selector: 'gtw-efile-edit-submission-table-data',
    templateUrl: './efile-edit-submission-table-data.component.html',
    styleUrls: ['./efile-edit-submission-table-data.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EfileEditSubmissionTableDataComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // --------------------------------------------------------------------------
  // Runtime context (populated from services in ngOnInit)
  // --------------------------------------------------------------------------
  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: ''
  };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  headers: any[] = [];

  // --------------------------------------------------------------------------
  // View-model
  // --------------------------------------------------------------------------
  mode: 'create' | 'edit' = 'create';
  title: string = 'Create Entry';
  minimise: boolean = false;
  crudLoading: boolean = false;
  saving: boolean = false;
  deleting: boolean = false;
  selectedTaxYear: string = '';
  showSub: boolean = true;
  submissionData: any[] = [];

  // Guards to prevent duplicate initialization if multiple change-detection
  // passes fire before activeScreen data is fully populated.
  private isInitialized = false;
  private isLoading = false;

  constructor(
    private efileEditSubmissionTableDataService: EfileEditSubmissionTableDataService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
  }

  // --------------------------------------------------------------------------
  // Context loader — single source of truth
  // --------------------------------------------------------------------------

  private applyWorkspaceContext(): void {
    this.isInitialized = false;

    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const gc = generalConfigFromGlobal(this.gtwGlobal);

    this.baseURLs = {
      api: gc.api,
      admin: gc.admin,
      custom: gc.custom,
      efile: gc.efile,
      pdfEngine: gc.pdfEngine,
      bulkPdfEngine: gc.bulkPdfEngine,
    };
    this.USER_SETTINGS = shell.userSettingsData;
    this.SERVER_CONFIG = shell.serverConfigData;
    this.GENERAL_CONFIG = shell.generalConfigData;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.headers = this.headersFromActiveScreen(activeScreen);

    this.tryInit();
    this.cdr.markForCheck();
  }

  /**
   * Reads the grid's column metadata that the originating screen stored on
   * `activeScreen.data.header` (singular key — same one the legacy grid uses
   * in `DataFiltersController`, `GridPivotService`, `GridBatchSaveController`,
   * etc.). Accept alias keys too so a future bridge variant doesn't silently
   * leave us with empty headers.
   */
  private headersFromActiveScreen(activeScreen: any): any[] {
    const d = activeScreen?.data || {};
    let raw: any = d.header ?? d.headerLabel ?? d.header_label ?? d.headers;
    if (typeof raw === 'string') {
      raw = extractObject(raw);
    }
    if (Array.isArray(raw) && raw.length > 0) {
      return raw;
    }
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length > 0) {
      return Object.values(raw);
    }
    return [];
  }

  // Gate that makes sure we only build the form once all context is in place.
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }
    if (
      !this.globalService ||
      !this.GENERAL_CONFIG ||
      !this.filterParams ||
      Object.keys(this.filterParams).length === 0 ||
      !this.headers ||
      !Array.isArray(this.headers) ||
      this.headers.length === 0
    ) {
      return;
    }
    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    try {
      this.mode = this.rowData && Object.keys(this.rowData).length > 0 ? 'edit' : 'create';
      this.title = this.mode === 'create' ? 'Create Entry' : 'Edit Entry';
      this.selectedTaxYear = this.filterParams.tax_year || '';
      this.initializeSubmissionData();
      this.cdr.detectChanges();
    } catch (e) {
      console.error('[EfileEditSubmissionTableData] Error in init:', e);
      this.cdr.detectChanges();
    }
  }

  // --------------------------------------------------------------------------
  // Form construction
  // --------------------------------------------------------------------------

  private initializeSubmissionData(): void {
    this.submissionData = [];
    if (!Array.isArray(this.headers) || this.headers.length === 0) return;

    const isEdit = this.rowData && Object.keys(this.rowData).length > 0;
    const rowKeys = isEdit ? Object.keys(this.rowData) : [];
    const rowValues = isEdit ? Object.values(this.rowData) : [];

    for (const header of this.headers) {
      if (!header || !header.label || !header.map || header.map === 'CREATED_TS') continue;

      const mapKey = header.map;
      const isSubmissionId = mapKey === 'SUBMISSION_ID';
      const isCreatedBy = mapKey === 'CREATED_BY';

      let value: any;
      if (isEdit) {
        const idx = rowKeys.indexOf(mapKey);
        const rawValue = idx >= 0 ? rowValues[idx] : null;
        value = isCreatedBy ? (this.USER_SETTINGS?.user?.sso_id || '') : (rawValue ?? null);
      } else {
        value = isCreatedBy ? (this.USER_SETTINGS?.user?.sso_id || null) : null;
      }

      this.submissionData.push({
        key: header.label,
        name: mapKey.toLowerCase(),
        value,
        disabled: isSubmissionId && this.mode === 'edit',
        hide: isSubmissionId && this.mode !== 'edit',
      });
    }
  }

  getSubmissionId(): string {
    const field = this.submissionData.find(item => item.name === 'submission_id');
    if (field && field.value != null && field.value !== '') {
      return field.value;
    }
    return '';
  }

  // --------------------------------------------------------------------------
  // Save / Delete / Cancel
  // --------------------------------------------------------------------------

  private prepareActionCall(mode: string): { url: string; params: any } {
    const CREATE_ACTION_CODE = 'A';
    const UPDATE_ACTION_CODE = 'E';
    const DELETE_ACTION_CODE = 'D';

    let actionCode: string;
    if (mode === 'create') actionCode = CREATE_ACTION_CODE;
    else if (mode === 'edit') actionCode = UPDATE_ACTION_CODE;
    else actionCode = DELETE_ACTION_CODE;

    const url = this.GENERAL_CONFIG.base_url + '/saveJsonObject?action_code=1x8oqq';

    const clobSettings = [{
      tax_year: this.filterParams.tax_year || '',
      scenario: this.filterParams.scenario || '',
      jcd_key: this.filterParams.jcd_key || ''
    }];

    const clobInput: any = {};
    for (const header of this.headers) {
      if (!header || !header.label || !header.map || header.map === 'CREATED_TS') continue;
      const key = header.map.toLowerCase();
      const match = this.submissionData.find(i => i.name === key);
      clobInput[key] = match ? match.value : '';
    }

    let params: any = _.merge({}, this.globalService || {}, this.filterParams);
    params = _.extend({ clob_data: JSON.stringify(clobInput) }, params);
    params = _.extend({ clob_settings: JSON.stringify(clobSettings) }, params);
    params.sso_id = this.USER_SETTINGS?.user?.sso_id || '';
    params.mode = actionCode;
    params.scenarioCode = this.filterParams.scenarioCode || this.globalService.scenarioCode || 'RAF';
    params.is_issue_key_enabled = this.filterParams.is_issue_key_enabled || this.globalService.is_issue_key_enabled || 'N';
    params.checkLockedbj = JSON.stringify([{ scenario_key: this.filterParams.scenario || this.globalService.scenario || 74 }]);

    return { url, params: sanitizeActionParams(params) };
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--red');
      return;
    }

    const { url, params } = this.prepareActionCall(this.mode);
    this.crudLoading = true;
    this.saving = true;
    this.cdr.detectChanges();

    this.efileEditSubmissionTableDataService.saveSubmissionTableData(
      url, params, this.globalService, this.filterParams, this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        this.saving = false;
        if (data.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
          // Emit 'refresh' so the hosting CeHostDialog closes and triggers a grid reload.
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error saving data', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('[EfileEditSubmissionTableData] Error saving data:', error);
        this.crudLoading = false;
        this.saving = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong while saving data, please try again!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  delete(): void {
    const submissionId = this.getSubmissionId();
    if (!submissionId) return;

    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we delete this request', 'gtw-snackbar--red');
      return;
    }

    const { url, params } = this.prepareActionCall('delete');
    this.crudLoading = true;
    this.deleting = true;
    this.cdr.detectChanges();

    this.efileEditSubmissionTableDataService.deleteSubmissionTableData(
      url, params, this.globalService, this.filterParams, this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.crudLoading = false;
        this.deleting = false;
        if (data.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data deleted successfully.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(data.errorMessage || 'Error deleting data', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('[EfileEditSubmissionTableData] Error deleting data:', error);
        this.crudLoading = false;
        this.deleting = false;
        this.gtwSnackbarService.openSnackBar('Something went wrong while deleting the data, please try again!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    // CeHostDialog listens for 'cancel' on the request-grid-update event and
    // closes the MatDialog with `{ action: 'close' }` — no direct dialog ref
    // needed from inside the custom element.
    this.requestGridUpdate.emit('cancel');
  }
}
